import React, { useState, useEffect, useMemo } from 'react';
import {
  Clock, Gauge, Zap, AlertTriangle, Loader2, Info, GitBranch, Coins, Activity,
} from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';
import { getLagerDurchlaufzeiten } from 'core/api/apiService';
import { getLeadTimeStatus, statusClasses } from '../../core/utils/statusColors';

/**
 * DurchlaufzeitenView — Lieferzeiten-Analyse (Stitch "Durchlaufzeiten – Analyse &
 * Lieferantenvergleich"). ECHT: geplante Lieferzeiten (EINE.APLFZ) je Warengruppe
 * + je Lieferant, Ampel grün/gelb/rot vs. Zielzeit. IST-Durchlaufzeit/Zuverlässigkeit/
 * Compliance/Trend = SAP-WE-Historie (EKBE) → ehrlich als „demnächst" markiert.
 */
const DurchlaufzeitenView = ({ user, selectedWerk }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('avg'); // avg | name
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    if (!selectedWerk) return undefined;
    let cancelled = false;
    setLoading(true); setError(null);
    getLagerDurchlaufzeiten(user, selectedWerk)
      .then((res) => { if (!cancelled) setData(res); })
      .catch((err) => { if (!cancelled) setError(err?.message || String(err)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user, selectedWerk]);

  const target = data?.target_days || 10;
  const byWg = useMemo(() => (Array.isArray(data?.byWarengruppe) ? data.byWarengruppe : []), [data]);
  const suppliers = useMemo(() => {
    const list = Array.isArray(data?.bySupplier) ? data.bySupplier.slice() : [];
    const dir = sortDir === 'asc' ? 1 : -1;
    list.sort((a, b) => (sortBy === 'name'
      ? String(a.lieferant_name).localeCompare(String(b.lieferant_name)) * dir
      : (a.avg - b.avg) * dir));
    return list;
  }, [data, sortBy, sortDir]);

  const maxWgAvg = Math.max(...byWg.map((w) => w.avg), target, 1);

  // ---- Styling ----
  const card = isDarkMode ? 'bg-white/6 border border-white/12 rounded-2xl p-4' : 'bg-white border border-slate-200 rounded-2xl p-4 shadow-sm';
  const labelCls = isDarkMode ? 'text-xs text-white/50 font-medium' : 'text-xs text-slate-500 font-medium';
  const mutedCls = isDarkMode ? 'text-white/45' : 'text-slate-400';
  const valCls = isDarkMode ? 'text-white/85' : 'text-slate-800';
  const inputCls = `px-3 py-2 rounded-lg border text-sm outline-none ${isDarkMode ? 'bg-white/8 border-white/15 text-white' : 'bg-white border-slate-200 text-slate-900'}`;

  const tooltipBox = isDarkMode
    ? 'pointer-events-none absolute left-0 top-full mt-1 w-60 p-2 rounded-lg text-[10px] leading-snug bg-gray-800 text-white/80 border border-white/10 opacity-0 group-hover:opacity-100 transition-opacity z-50 shadow-lg'
    : 'pointer-events-none absolute left-0 top-full mt-1 w-60 p-2 rounded-lg text-[10px] leading-snug bg-slate-800 text-white opacity-0 group-hover:opacity-100 transition-opacity z-50 shadow-lg';

  const KpiTile = ({ status, Icon, label, value, sub, info }) => {
    const a = statusClasses(status, isDarkMode);
    return (
      <div className={`relative overflow-hidden rounded-2xl p-4 bg-gradient-to-br ${a.tint} to-transparent ${isDarkMode ? 'bg-white/[0.04] border border-white/12' : 'bg-white border border-slate-200 shadow-sm'}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className={`${labelCls} flex items-center gap-1`}>
              <span className="truncate">{label}</span>
              {info && (
                <span className="relative group flex-shrink-0">
                  <Info className="w-3 h-3 cursor-help opacity-50 hover:opacity-90" />
                  <span className={tooltipBox}>{info}</span>
                </span>
              )}
            </div>
            <div className={`text-2xl font-bold mt-0.5 ${a.val}`}>{value}</div>
            {sub && <div className={`text-xs ${mutedCls} truncate`}>{sub}</div>}
          </div>
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${a.wrap}`}><Icon className="w-5 h-5" /></div>
        </div>
      </div>
    );
  };

  const StatusBadge = ({ status }) => {
    const a = statusClasses(status, isDarkMode);
    const label = status === 'green' ? t('lm.dlzOk') : status === 'yellow' ? t('lm.dlzWatch') : status === 'red' ? t('lm.dlzRisk') : '—';
    return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${a.badge}`}>{label}</span>;
  };

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(col); setSortDir(col === 'name' ? 'asc' : 'asc'); }
  };

  const kpis = data?.kpis;
  const overallStatus = getLeadTimeStatus(kpis?.overall_avg, target);
  const slowestStatus = kpis?.slowest ? getLeadTimeStatus(kpis.slowest.avg, target) : 'neutral';
  // Sourcing-/Kosten-KPIs (research-basiert, real)
  const ss = kpis?.single_source || { count: 0, total: 0 };
  const ssStatus = ss.count === 0 ? 'green' : (ss.total > 0 && ss.count / ss.total > 0.3) ? 'red' : 'yellow';
  const ps = kpis?.price_spread || { materials_with_choice: 0, median_spread_pct: 0 };
  const leadSpread = (kpis?.slowest && kpis?.fastest) ? Math.round((kpis.slowest.avg - kpis.fastest.avg) * 10) / 10 : 0;

  return (
    <div className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto space-y-5">
        <div>
          <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>{t('lm.dlzTitle')}</h2>
          <p className={`text-sm ${mutedCls}`}>{t('lm.dlzSubtitle', { target })}</p>
        </div>

        {!selectedWerk ? (
          <div className={`flex flex-col items-center justify-center gap-3 py-16 ${mutedCls}`}><Clock className="w-10 h-10" /> <span className="text-sm">{t('lm.dlzNoWerk')}</span></div>
        ) : loading ? (
          <div className={`flex items-center justify-center gap-2 py-16 ${mutedCls}`}><Loader2 className="w-5 h-5 animate-spin" /> <span className="text-sm">{t('lm.loading')}</span></div>
        ) : error ? (
          <div className={`flex flex-col items-center justify-center gap-2 py-16 ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}><AlertTriangle className="w-8 h-8" /> <span className="text-sm">{error}</span></div>
        ) : byWg.length === 0 ? (
          <div className={`flex flex-col items-center justify-center gap-3 py-16 ${mutedCls}`}><Clock className="w-10 h-10" /> <span className="text-sm">{t('lm.dlzEmpty')}</span></div>
        ) : (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <KpiTile status={overallStatus} Icon={Gauge} label={t('lm.dlzKpiAvg')} value={`${kpis?.overall_avg ?? 0} ${t('lm.dlzDays')}`} sub={t('lm.dlzTargetSub', { target })} info={t('lm.dlzKpiAvgInfo')} />
              <KpiTile status="green" Icon={Zap} label={t('lm.dlzKpiFastest')} value={`${kpis?.fastest?.avg ?? 0} ${t('lm.dlzDays')}`} sub={kpis?.fastest?.warengruppe} info={t('lm.dlzKpiFastestInfo')} />
              <KpiTile status={slowestStatus} Icon={Clock} label={t('lm.dlzKpiSlowest')} value={`${kpis?.slowest?.avg ?? 0} ${t('lm.dlzDays')}`} sub={kpis?.slowest?.warengruppe} info={t('lm.dlzKpiSlowestInfo')} />
              <KpiTile status={kpis?.risk_count > 0 ? 'red' : 'green'} Icon={AlertTriangle} label={t('lm.dlzKpiRisk')} value={kpis?.risk_count ?? 0} sub={t('lm.dlzKpiRiskSub', { target })} info={t('lm.dlzKpiRiskInfo', { target })} />
            </div>

            {/* KPIs — Sourcing & Kosten (research-basiert) */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <KpiTile status={ssStatus} Icon={GitBranch} label={t('lm.dlzKpiSingleSource')} value={ss.count} sub={t('lm.dlzKpiSingleSourceSub', { total: ss.total })} info={t('lm.dlzKpiSingleSourceInfo')} />
              <KpiTile status="green" Icon={Coins} label={t('lm.dlzKpiSavings')} value={`${ps.median_spread_pct}%`} sub={t('lm.dlzKpiSavingsSub', { count: ps.materials_with_choice })} info={t('lm.dlzKpiSavingsInfo')} />
              <KpiTile status="neutral" Icon={Activity} label={t('lm.dlzKpiSpread')} value={`${leadSpread} ${t('lm.dlzDays')}`} sub={t('lm.dlzKpiSpreadSub')} info={t('lm.dlzKpiSpreadInfo')} />
            </div>

            {/* Lead-Time je Warengruppe — horizontale Ampel-Balken */}
            <div className={card}>
              <div className="flex items-center justify-between mb-3">
                <h3 className={`text-sm font-semibold ${valCls}`}>{t('lm.dlzByWg')}</h3>
                <span className={`text-xs ${mutedCls}`}>{t('lm.dlzTargetSub', { target })}</span>
              </div>
              <div className="space-y-2.5">
                {byWg.map((w) => {
                  const st = getLeadTimeStatus(w.avg, target);
                  const a = statusClasses(st, isDarkMode);
                  const pct = Math.max(Math.round((w.avg / maxWgAvg) * 100), 3);
                  return (
                    <div key={w.matkl} className="flex items-center gap-3">
                      <div className={`w-40 text-xs truncate ${valCls}`} title={w.warengruppe}>{w.warengruppe}</div>
                      <div className={`flex-1 h-5 rounded-md overflow-hidden ${isDarkMode ? 'bg-white/8' : 'bg-slate-100'} relative`}>
                        <div className={`h-full ${a.bar} rounded-md transition-all`} style={{ width: `${pct}%` }} />
                        {/* Ziel-Marke */}
                        <div className="absolute top-0 bottom-0 border-l border-dashed border-slate-400/70" style={{ left: `${Math.min(Math.round((target / maxWgAvg) * 100), 100)}%` }} title={`${t('lm.dlzColZiel')}: ${target}`} />
                      </div>
                      <div className="w-28 text-right">
                        <div className={`text-xs font-semibold ${a.val}`}>{w.avg} {t('lm.dlzDays')}</div>
                        <div className={`text-[10px] ${mutedCls}`}>{w.min}–{w.max} {t('lm.dlzDays')}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className={`mt-2 text-[10px] ${mutedCls}`}>{t('lm.dlzLegende')}</div>
            </div>

            {/* Lieferanten-Vergleich */}
            <div className={card}>
              <h3 className={`text-sm font-semibold mb-3 ${valCls}`}>{t('lm.dlzSupplierTitle')} ({suppliers.length})</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className={`border-b ${isDarkMode ? 'border-white/10' : 'border-slate-200'} ${mutedCls}`}>
                      <th className="text-left pb-2 pr-2 font-semibold cursor-pointer select-none" onClick={() => toggleSort('name')}>{t('lm.dlzColLieferant')} {sortBy === 'name' ? (sortDir === 'asc' ? '↑' : '↓') : ''}</th>
                      <th className="text-right pb-2 pr-2 font-semibold">{t('lm.dlzColMaterialien')}</th>
                      <th className="text-right pb-2 pr-2 font-semibold">{t('lm.dlzColWg')}</th>
                      <th className="text-right pb-2 pr-2 font-semibold cursor-pointer select-none" onClick={() => toggleSort('avg')}>{t('lm.dlzColLieferzeit')} {sortBy === 'avg' ? (sortDir === 'asc' ? '↑' : '↓') : ''}</th>
                      <th className="text-left pb-2 font-semibold">{t('lm.dlzColStatus')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {suppliers.map((s) => {
                      const st = getLeadTimeStatus(s.avg, target);
                      const a = statusClasses(st, isDarkMode);
                      return (
                        <tr key={s.lifnr} className={`border-b ${isDarkMode ? 'border-white/5' : 'border-slate-50'} ${valCls}`}>
                          <td className="py-1.5 pr-2">{s.lieferant_name}</td>
                          <td className="py-1.5 pr-2 text-right">{s.materialien}</td>
                          <td className="py-1.5 pr-2 text-right">{s.warengruppen}</td>
                          <td className={`py-1.5 pr-2 text-right font-semibold ${a.val}`}>{s.avg} {t('lm.dlzDays')}</td>
                          <td className="py-1.5"><StatusBadge status={st} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Ehrlicher Hinweis: Performance-Daten brauchen SAP-WE-Historie */}
            <div className={`flex items-start gap-2 text-xs rounded-xl p-3 ${isDarkMode ? 'bg-white/5 text-white/55' : 'bg-slate-50 text-slate-500'}`}>
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{t('lm.dlzSapHint')}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DurchlaufzeitenView;
